import { DriverRoadDistanceState } from '../types/roadDistance'

type SuccessfulRoadDistance = Extract<DriverRoadDistanceState, { status: 'success' }>

/** A routed value is authoritative only for the exact driver fix used to calculate it. */
export const isCurrentRoadDistance = (currentDriverFixAt: string | null | undefined, state: DriverRoadDistanceState): state is SuccessfulRoadDistance =>
    state.status === 'success' && currentDriverFixAt != null && state.driverLocationFixAt === currentDriverFixAt

export interface ResolvedMilesOut {
    milesOut: number | null
    milesOutApproximate: boolean
}

/**
 * The ONE miles-out resolution behind the drivers table, Copy Load and the {{miles_out}} email variable:
 * GraphHopper truck-road miles when the route belongs to the driver's CURRENT location fix, otherwise the
 * direct estimate, marked approximate.
 *
 * Pending, failed and superseded routes all fall back to that estimate rather than to nothing. A blank or
 * `-` miles-out line is the one outcome nobody can work with — the broker reads it as "no answer", while
 * a direct estimate is both true and immediately useful.
 *
 * Whether the estimate is VISIBLY marked is the formatter's call, not this one's: `formatMilesOutLabel`
 * adds the ≈ for the operator's table, `formatMilesOut` leaves it off for the broker email and clipboard.
 */
export const resolveMilesOut = (
    currentDriverFixAt: string | null | undefined,
    proximityDistanceMi: number | null,
    state: DriverRoadDistanceState
): ResolvedMilesOut => {
    if (isCurrentRoadDistance(currentDriverFixAt, state)) return { milesOut: state.roadDistanceMi, milesOutApproximate: false }
    if (proximityDistanceMi != null) return { milesOut: proximityDistanceMi, milesOutApproximate: true }
    return { milesOut: null, milesOutApproximate: false }
}

/**
 * "41.2 mi" — the number on its own, for text that LEAVES Numeo: the broker email and the clipboard.
 * No ≈ there: the broker has no idea what it means, and it reads as hedging on the one number they
 * are scanning for. Null when there is no distance at all. `fractionDigits: 0` gives whole miles
 * ("41 mi"), which is what the Offer email's {{miles_out}} quotes.
 *
 * `en-US` is pinned, not left to the browser: this string is read by a US broker, and a dispatcher on a
 * de-DE/ru-RU machine would otherwise quote `40,3 mi` — or `1.042,7 mi`, which reads as a thousands
 * separator. Same rule as fmtMiles in ./format.ts and the money formatter in ./bidFormat.ts.
 */
export const formatMilesOut = ({ milesOut }: Pick<ResolvedMilesOut, 'milesOut'>, fractionDigits = 1): string | null =>
    milesOut == null ? null : `${milesOut.toLocaleString('en-US', { maximumFractionDigits: fractionDigits })} mi`

/** "≈41.2 mi" — the OPERATOR-facing label. The ≈ is their cue that it is the direct estimate, not the route. */
export const formatMilesOutLabel = (resolved: ResolvedMilesOut): string | null => {
    const value = formatMilesOut(resolved)
    return value == null ? null : `${resolved.milesOutApproximate ? '≈' : ''}${value}`
}
